import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  changeLanguage,
  getCurrentLanguage,
  getSupportedLanguages,
  getLanguageDisplayName,
} from '../config/i18n';

// The document title is owned by usePageTitle (route-aware: "GeoMarkup - <Page>").
// Here we only keep the meta description in sync with the active language.
const APP_META_DESCRIPTION_KEY = 'app.metaDescription';
const META_DESCRIPTION_SELECTOR = 'meta[name="description"]';

// Define the context interface
export interface I18nContextValue {
  currentLanguage: string;
  supportedLanguages: string[];
  changeLanguage: (language: string) => Promise<void>;
  getLanguageDisplayName: (language: string) => string;
  isReady: boolean;
}

// Create the context
// eslint-disable-next-line react-refresh/only-export-components
export const I18nContext = createContext<I18nContextValue | null>(null);

// Props interface for the provider
interface I18nProviderProps {
  children: ReactNode;
}

// I18n Provider component
export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [isReady, setIsReady] = useState<boolean>(false);
  const [currentLanguage, setCurrentLanguage] =
    useState<string>(getCurrentLanguage());

  // Effect to handle i18n initialization and language changes
  useEffect(() => {
    // Set ready state when i18n is initialized
    if (i18n.isInitialized) {
      setIsReady(true);
      setCurrentLanguage(i18n.language);
    }

    // Language change handler
    const handleLanguageChanged = (language: string) => {
      setCurrentLanguage(language);

      // Keep the meta description in sync with the active language
      if (i18n.exists(APP_META_DESCRIPTION_KEY)) {
        const metaDescription = document.querySelector(
          META_DESCRIPTION_SELECTOR
        );
        metaDescription?.setAttribute(
          'content',
          i18n.t(APP_META_DESCRIPTION_KEY)
        );
      }
    };

    // Set up event listeners
    i18n.on('initialized', () => {
      setIsReady(true);
      setCurrentLanguage(i18n.language);
    });

    i18n.on('languageChanged', handleLanguageChanged);

    // Initial setup if already initialized
    if (i18n.isInitialized) {
      handleLanguageChanged(i18n.language);
    }

    // Cleanup event listeners
    return () => {
      i18n.off('initialized');
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // Handle language change with error handling.
  // Stable reference so consumers (e.g. useApplyUserPreferences) don't re-run
  // effects on every provider re-render.
  const handleChangeLanguage = useCallback(
    async (language: string): Promise<void> => {
      try {
        await changeLanguage(language);
      } catch (error) {
        console.error('Failed to change language:', error);
        throw error;
      }
    },
    []
  );

  // Context value — memoized so a re-render of this provider (which wraps
  // MantineProvider) doesn't cascade re-renders through the whole tree.
  const contextValue = useMemo<I18nContextValue>(
    () => ({
      currentLanguage,
      supportedLanguages: getSupportedLanguages(),
      changeLanguage: handleChangeLanguage,
      getLanguageDisplayName,
      isReady,
    }),
    [currentLanguage, isReady, handleChangeLanguage]
  );

  return <I18nContext value={contextValue}>{children}</I18nContext>;
};
